import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * A small, accessible "?" help icon that reveals a styled popover.
 *
 * Opens on mouse hover, keyboard focus, OR click/tap (so it works for mouse,
 * keyboard, and touch users alike — unlike a native `title` tooltip, which is
 * slow, unstyled, and absent on touch). Closes on mouse leave, blur, Escape,
 * or a second click.
 *
 * The popover is positioned relative to the icon and flips to stay inside the
 * viewport, so it is not clipped by a scrollable modal.
 *
 * Props:
 *   content   - string or JSX shown inside the popover
 *   label     - accessible label for the icon (default "Help")
 *   maxWidth  - popover max width in px (default 280)
 */
const InfoTooltip = ({ content, label = 'Help', maxWidth = 440 }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, placeAbove: false, placeLeft: false });
  const iconRef = useRef(null);

  const computePosition = useCallback(() => {
    const el = iconRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 3;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Prefer below-right of the icon; flip if there isn't room.
    const placeAbove = r.bottom + 140 > vh && r.top > 140;
    const placeLeft = r.left + maxWidth + margin > vw;
    setPos({
      top: placeAbove ? r.top - margin : r.bottom + margin,
      left: placeLeft ? r.right : r.left,
      placeAbove,
      placeLeft,
    });
  }, [maxWidth]);

  const show = useCallback(() => {
    computePosition();
    setOpen(true);
  }, [computePosition]);

  const hide = useCallback(() => setOpen(false), []);

  // Close on Escape and reposition on scroll/resize while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') hide(); };
    const onMove = () => computePosition();
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, hide, computePosition]);

  const popoverStyle = {
    position: 'fixed',
    top: pos.top,
    left: pos.left,
    transform: `translate(${pos.placeLeft ? '-100%' : '0'}, ${pos.placeAbove ? '-100%' : '0'})`,
    width: 'max-content',
    minWidth: `${Math.min(maxWidth, 360)}px`,
    maxWidth: `${maxWidth}px`,
    backgroundColor: '#2c3e50',
    color: '#fff',
    padding: '10px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    lineHeight: '1.5',
    fontWeight: 400,
    textTransform: 'none',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
    zIndex: 10001,
    whiteSpace: 'normal',
    pointerEvents: 'none',
  };

  return (
    <span
      ref={iconRef}
      role="button"
      tabIndex={0}
      aria-label={label}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={(e) => { e.stopPropagation(); open ? hide() : show(); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open ? hide() : show(); }
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '15px',
        height: '15px',
        borderRadius: '50%',
        backgroundColor: open ? '#667eea' : '#bdc3c7',
        color: '#fff',
        fontSize: '10px',
        fontWeight: 'bold',
        lineHeight: 1,
        cursor: 'help',
        flexShrink: 0,
        userSelect: 'none',
        transition: 'background-color 0.15s',
      }}
    >
      ?
      {open && createPortal(<span style={popoverStyle}>{content}</span>, document.body)}
    </span>
  );
};

export default InfoTooltip;
